import { useCallback, useEffect, useRef, useState } from "react";
import { type ApiError, isAbortError } from "@/lib/api/errors";
import type {
  BLRelatedDocumentItem,
  BLRelatedDocumentsResponse,
} from "@/lib/api/extract";
import {
  getBlRelatedDocuments,
  getSourceDocumentFile,
  getSourceDocumentReport,
} from "@/lib/api/extract";
import { useLatest } from "@/lib/hooks/useLatest";
import { triggerPdfDownload } from "@/lib/pdf";

export type BlDocumentsState = {
  data: BLRelatedDocumentsResponse | null;
  loading: boolean;
  errorMessage: string | null;
  hasLoaded: boolean;
};

type UseBlDocumentsOptions = {
  onUnauthorized: (message?: string) => void;
};

type UseBlDocumentsResult = {
  stateByBl: Record<string, BlDocumentsState>;
  downloadingSha: string | null;
  downloadingReportSha: string | null;
  downloadErrorMessage: string | null;
  ensureDocuments: (bl: string, options?: { force?: boolean }) => Promise<void>;
  downloadDocument: (document: BLRelatedDocumentItem) => Promise<void>;
  downloadDocumentReport: (document: BLRelatedDocumentItem) => Promise<void>;
  clearDownloadError: () => void;
  invalidateAll: () => void;
};

const EMPTY_STATE: BlDocumentsState = {
  data: null,
  loading: false,
  errorMessage: null,
  hasLoaded: false,
};

export function useBlDocuments({
  onUnauthorized,
}: UseBlDocumentsOptions): UseBlDocumentsResult {
  const [stateByBl, setStateByBl] = useState<Record<string, BlDocumentsState>>(
    {},
  );
  const [downloadingSha, setDownloadingSha] = useState<string | null>(null);
  const [downloadingReportSha, setDownloadingReportSha] = useState<
    string | null
  >(null);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState<
    string | null
  >(null);
  const stateByBlRef = useLatest(stateByBl);
  const onUnauthorizedRef = useLatest(onUnauthorized);
  const requestsRef = useRef<Map<string, Promise<void>>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const ensureDocuments = useCallback(
    async (bl: string, options: { force?: boolean } = {}) => {
      const normalizedBl = bl.trim();
      if (!normalizedBl) return;

      const current = stateByBlRef.current[normalizedBl] ?? EMPTY_STATE;
      if (!options.force && (current.loading || current.hasLoaded)) {
        return;
      }

      const inFlight = requestsRef.current.get(normalizedBl);
      if (inFlight) {
        return inFlight;
      }

      const abortController = new AbortController();
      abortControllersRef.current.get(normalizedBl)?.abort();
      abortControllersRef.current.set(normalizedBl, abortController);

      setStateByBl((prev) => ({
        ...prev,
        [normalizedBl]: {
          ...(prev[normalizedBl] ?? EMPTY_STATE),
          loading: true,
          errorMessage: null,
        },
      }));

      let request: Promise<void> | null = null;
      request = (async () => {
        try {
          const response = await getBlRelatedDocuments(normalizedBl, {
            signal: abortController.signal,
          });
          setStateByBl((prev) => ({
            ...prev,
            [normalizedBl]: {
              data: response,
              loading: false,
              errorMessage: null,
              hasLoaded: true,
            },
          }));
        } catch (error) {
          if (isAbortError(error)) {
            return;
          }

          const apiError = error as ApiError;
          if (apiError.status === 401) {
            onUnauthorizedRef.current();
            return;
          }

          setStateByBl((prev) => ({
            ...prev,
            [normalizedBl]: {
              ...(prev[normalizedBl] ?? EMPTY_STATE),
              loading: false,
              errorMessage:
                apiError.message ??
                "Impossibile recuperare i documenti collegati al BL.",
              hasLoaded: true,
            },
          }));
        } finally {
          if (
            abortControllersRef.current.get(normalizedBl) === abortController
          ) {
            abortControllersRef.current.delete(normalizedBl);
          }
          if (requestsRef.current.get(normalizedBl) === request) {
            requestsRef.current.delete(normalizedBl);
          }
        }
      })();

      requestsRef.current.set(normalizedBl, request as Promise<void>);
      return request as Promise<void>;
    },
    [onUnauthorizedRef, stateByBlRef],
  );

  const downloadDocument = useCallback(
    async (document: BLRelatedDocumentItem) => {
      try {
        setDownloadingSha(document.source_document_sha256);
        setDownloadErrorMessage(null);
        const response = await getSourceDocumentFile(
          document.source_document_sha256,
        );
        triggerPdfDownload(
          response.pdf_base64,
          response.input_filename ??
            document.input_filename ??
            `source-document-${document.source_document_sha256}.pdf`,
        );
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          onUnauthorizedRef.current();
          return;
        }
        setDownloadErrorMessage(
          apiError.message ?? "Impossibile scaricare il documento selezionato.",
        );
      } finally {
        setDownloadingSha(null);
      }
    },
    [onUnauthorizedRef],
  );

  const downloadDocumentReport = useCallback(
    async (document: BLRelatedDocumentItem) => {
      try {
        setDownloadingReportSha(document.source_document_sha256);
        setDownloadErrorMessage(null);
        const response = await getSourceDocumentReport(
          document.source_document_sha256,
        );
        const baseFileName =
          response.input_filename ??
          document.input_filename ??
          `source-document-${document.source_document_sha256}`;
        const reportFileName = baseFileName.toLowerCase().endsWith(".pdf")
          ? baseFileName.replace(/\.pdf$/i, "-report.pdf")
          : `${baseFileName}-report.pdf`;
        triggerPdfDownload(response.pdf_base64, reportFileName);
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          onUnauthorizedRef.current();
          return;
        }
        setDownloadErrorMessage(
          apiError.message ?? "Impossibile scaricare il report selezionato.",
        );
      } finally {
        setDownloadingReportSha(null);
      }
    },
    [onUnauthorizedRef],
  );

  const clearDownloadError = useCallback(() => {
    setDownloadErrorMessage(null);
  }, []);

  const invalidateAll = useCallback(() => {
    requestsRef.current.clear();
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
    setStateByBl({});
  }, []);

  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      abortControllersRef.current.clear();
      requestsRef.current.clear();
    };
  }, []);

  return {
    stateByBl,
    downloadingSha,
    downloadingReportSha,
    downloadErrorMessage,
    ensureDocuments,
    downloadDocument,
    downloadDocumentReport,
    clearDownloadError,
    invalidateAll,
  };
}
